import { useNavigate } from "react-router-dom";
import {
    FaMicrophone,
    FaComments,
    FaSlack,
    FaGoogle,
    FaMailBulk,
    FaEdit,
    FaCar,
} from "react-icons/fa";
import { FaBowlFood, FaLinkedin, FaUber, FaKitMedical, FaProfil } from "react-icons/fa6"
import { SiSwiggy, SiJira } from "react-icons/si"
import { IoPersonCircleOutline } from "react-icons/io5"
import { RiNotionLine } from "react-icons/ri"
import { BiLogOut } from "react-icons/bi";
import { motion } from "framer-motion";
import { useTheme } from "../theme/theme-provider";
import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../utils/authentication";

const InteractionCard = ({ icon: Icon, title, onClick }) => (
    <motion.button
        onClick={onClick}
        className="flex flex-col items-center justify-center p-8 md:p-12
                   bg-gradient-to-br from-white/10 to-white/5 dark:from-gray-800 dark:to-gray-700
                   backdrop-blur-lg rounded-2xl cursor-pointer transition-all duration-300
                   hover:bg-gradient-to-br hover:from-primary-100/20 hover:to-primary-50/10
                   dark:hover:from-primary-900/20 dark:hover:to-gray-800
                   border border-white/20 dark:border-gray-600 shadow-lg"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        aria-label={`Choose ${title} as interaction mode`}
    >
        <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.5 }}>
            <Icon size={60} className="text-primary-500" />
        </motion.div>
        <h3 className="mt-6 text-xl font-semibold text-gray-900 dark:text-gray-100">
            {title}
        </h3>
    </motion.button>
);

const IntegrationCard = ({ service, isConnected, onConnect, icon: Icon }) => (
    <motion.div
        className="bg-gradient-to-br from-white/5 to-white/10
                   dark:from-gray-800 dark:to-gray-700
                   backdrop-blur-lg rounded-xl p-6
                   border border-white/10 dark:border-gray-600
                   hover:border-primary-500/50 transition-all duration-300 shadow-lg"
        whileHover={{ scale: 1.02 }}
    >
        <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
                <Icon size={24} className="text-primary-500" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {service}
                </h3>
            </div>
            <div
                className={`h-3 w-3 rounded-full ${isConnected ? "bg-green-500" : "bg-red-500"
                    }`}
                aria-hidden="true"
            >
                <span className="sr-only">{isConnected ? "Connected" : "Disconnected"}</span>
            </div>
        </div>
        <button
            onClick={onConnect}
            className={`w-full py-2 px-4 rounded-lg text-sm font-medium
                       transition-colors duration-200 ${isConnected
                    ? "bg-green-500/10 text-green-500 hover:bg-green-500/20"
                    : "bg-primary-500/10 text-primary-500 hover:bg-primary-500/20"
                }`}
            aria-pressed={isConnected}
        >
            {isConnected ? "Connected" : "Connect"}
        </button>
    </motion.div>
);

const OptionsPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const [integrations, setIntegrations] = useState([]);
    const { logout } = useAuth();

    const fetchIntegrations = async () => {
        try {
            const userId = localStorage.getItem("userId");
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await axios.get(
                `${apiUrl}/integrations/${userId}`
            );
            if (response.data.integrations) {
                setIntegrations(response.data.integrations);
            } else {
                console.error("Integrations not found.");
            }
        } catch (error) {
            console.error("Error fetching integrations:", error);
        }
    };

    const isIntegrationConnected = (integrations, provider) => {
        return integrations?.some(
            (integration) =>
                integration.Provider.toLowerCase() === provider.toLowerCase()
        );
    };

    const handleServiceConnect = async (service) => {
        const apiUrl = process.env.REACT_APP_API_URL;
        const userId = localStorage.getItem("userId");
        try {
            window.location.href = `${apiUrl}/integrations/${service}/connect/${userId}`;
        } catch (error) {
            console.error(`Error connecting ${service}:`, error);
        }
    };

    const handleLogout = () => {
        logout();
    };

    useEffect(() => {
        fetchIntegrations();
    }, []);

    return (
        <div
            className="min-h-screen bg-gradient-to-br from-background-light to-primary-50
                  dark:from-background-dark dark:to-primary-900
                  transition-colors duration-300"
        >

            {/* Profile Button */}
            {/* <motion.button
                onClick={() => navigate("/profile")}
                className="absolute top-4 right-32 flex items-center gap-2 px-3 py-2
                   rounded-full border-2 border-gray-400
                   bg-gray-400/10 text-gray-400
                   hover:border-blue-500 hover:bg-blue-500/10 hover:text-blue-500
                   transition-all duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
            >
                <IoPersonCircleOutline className="w-5 h-5" />
                <span className="text-sm font-medium">Profile</span>
            </motion.button> */}

            {/* Logout Button */}
            <motion.button
                onClick={handleLogout}
                className="absolute top-4 right-4 flex items-center gap-2 px-3 py-2
                   rounded-full border-2 border-gray-400
                   bg-gray-400/10 text-gray-400
                   hover:border-red-500 hover:bg-red-500/10 hover:text-red-500
                   transition-all duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Logout"
            >
                <BiLogOut className="w-5 h-5" />
                <span className="text-sm font-medium">Logout</span>
            </motion.button>

            <div className="container mx-auto min-h-screen flex flex-col justify-evenly">
                {/* Interaction Options */}
                <section className="mt-12">
                    <motion.h2
                        className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-8 text-center"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        Choose Your Interaction Mode
                    </motion.h2>

                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        <InteractionCard
                            icon={FaMicrophone}
                            title="Voice Assistant"
                            onClick={() => navigate("/realtime")}
                        />
                        <InteractionCard
                            icon={FaComments}
                            title="Chat Interface"
                            onClick={() => navigate("/chat")}
                        />
                    </div>
                </section>

                {/* Integrations */}
                <section className="mt-12 mb-12">
                    <motion.h2
                        className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8 text-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                    >
                        Service Integrations
                    </motion.h2>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                        <IntegrationCard
                            service="Gmail"
                            icon={FaMailBulk}
                            isConnected={isIntegrationConnected(
                                integrations,
                                "gmail"
                            )}
                            onConnect={() => {
                                /* handle connection */
                                if (
                                    !isIntegrationConnected(
                                        integrations,
                                        "gmail"
                                    )
                                ) {
                                    handleServiceConnect("gmail");
                                }
                            }}
                        />
                        <IntegrationCard
                            service="Google Calendar"
                            icon={FaGoogle}
                            isConnected={isIntegrationConnected(
                                integrations,
                                "google_calendar"
                            )}
                            onConnect={() => {
                                /* handle connection */
                                if (
                                    !isIntegrationConnected(
                                        integrations,
                                        "google_calendar"
                                    )
                                ) {
                                    handleServiceConnect("calendar");
                                }
                            }}
                        />
                        <IntegrationCard
                            service="Slack"
                            icon={FaSlack}
                            isConnected={isIntegrationConnected(
                                integrations,
                                "slack"
                            )}
                            onConnect={() => {
                                /* handle connection */
                                if (
                                    !isIntegrationConnected(
                                        integrations,
                                        "slack"
                                    )
                                ) {
                                    handleServiceConnect("slack");
                                }
                            }}
                        />
                        <IntegrationCard
                            service="Notion"
                            icon={RiNotionLine}
                            isConnected={isIntegrationConnected(
                                integrations,
                                "notion"
                            )}
                            onConnect={() => {
                                /* handle connection */
                                if (
                                    !isIntegrationConnected(
                                        integrations,
                                        "notion"
                                    )
                                ) {
                                    handleServiceConnect("notion");
                                }
                            }}
                        />
                        <IntegrationCard
                            service="Jira"
                            icon={SiJira}
                            isConnected={false}
                            onConnect={() => { }}
                        />
                        <IntegrationCard
                            service="Uber"
                            icon={FaUber}
                            isConnected={false}
                            onConnect={() => { }}
                        />
                        <IntegrationCard
                            service="Swiggy"
                            icon={SiSwiggy}
                            isConnected={false}
                            onConnect={() => { }}
                        />
                        <IntegrationCard
                            service="LinkedIn"
                            icon={FaLinkedin}
                            isConnected={false}
                            onConnect={() => { }}
                        />
                        <IntegrationCard
                            service="1mg"
                            icon={FaKitMedical}
                            isConnected={false}
                            onConnect={() => { }}
                        />
                    </div>
                </section>
            </div>
        </div>
    );
};

export default OptionsPage;
